'use client';
import React, { useRef } from 'react';
import Link from 'next/link';

interface InterestCategory {
  id: string;
  label: string;
  image: string;
  alt: string;
  href: string;
  count: string;
}

const interests: InterestCategory[] = [
{
  id: 'music',
  label: 'Music',
  image: "https://images.unsplash.com/photo-1662867159931-f5de37bb08ee",
  alt: 'Crowd at outdoor music concert with hands raised and stage lights',
  href: '#music',
  count: '2,400+ events'
},
{
  id: 'tech',
  label: 'Tech & Innovation',
  image: 'https://img.rocket.new/generatedImages/rocket_gen_img_1ddde7145-1772221223878.png',
  alt: 'Technology innovation conference with circuit board and digital displays',
  href: '#tech',
  count: '890+ events'
},
{
  id: 'food',
  label: 'Food & Drink',
  image: "https://images.unsplash.com/photo-1660511057210-f7fba78d2741",
  alt: 'Gourmet food and wine tasting event with elegant table setting',
  href: '#food',
  count: '1,200+ events'
},
{
  id: 'sports',
  label: 'Sports',
  image: "https://images.unsplash.com/photo-1461897104016-0b3b00cc81ee",
  alt: 'Athletic sports event with runners competing on track',
  href: '#sports',
  count: '3,100+ events'
},
{
  id: 'arts',
  label: 'Arts & Culture',
  image: "https://images.unsplash.com/photo-1734283093134-9d0d86c4da27",
  alt: 'Art gallery opening with visitors admiring colorful paintings',
  href: '#arts',
  count: '760+ events'
},
{
  id: 'outdoor',
  label: 'Outdoor & Adventure',
  image: "https://images.unsplash.com/photo-1560843552-d4bbb846a8d8",
  alt: 'Outdoor adventure hiking group on mountain trail with scenic view',
  href: '#outdoor',
  count: '540+ events'
}];


export default function InterestCarousel() {
  const scrollRef = useRef<HTMLDivElement>(null);

  const scroll = (dir: 'left' | 'right') => {
    if (scrollRef.current) {
      scrollRef.current.scrollBy({ left: dir === 'left' ? -340 : 340, behavior: 'smooth' });
    }
  };

  return (
    <div className="py-10" style={{ background: '#FAF7F2' }}>
      <div style={{ margin: '0 48px' }}>
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-4">
            <div style={{ width: '3px', height: '28px', background: 'linear-gradient(to bottom, #C9A84C, #8B6914)', borderRadius: '2px' }} />
            <h2
              className="text-2xl font-bold"
              style={{ fontFamily: '"Playfair Display", Georgia, serif', color: '#1A0A00' }}>
              
              Browse by interest
            </h2>
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => scroll('left')}
              className="w-9 h-9 flex items-center justify-center rounded-full transition-colors"
              style={{ border: '1px solid rgba(201,168,76,0.3)', background: 'transparent' }}
              onMouseEnter={(e) => (e.currentTarget.style.background = 'rgba(201,168,76,0.1)')}
              onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}>
              
              <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                <path d="M9 3L5 7L9 11" stroke="#C9A84C" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </button>
            <button
              onClick={() => scroll('right')}
              className="w-9 h-9 flex items-center justify-center rounded-full transition-colors"
              style={{ border: '1px solid rgba(201,168,76,0.3)', background: 'transparent' }}
              onMouseEnter={(e) => (e.currentTarget.style.background = 'rgba(201,168,76,0.1)')}
              onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}>
              
              <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                <path d="M5 3L9 7L5 11" stroke="#C9A84C" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </button>
          </div>
        </div>

        {/* Carousel */}
        <div
          ref={scrollRef}
          className="flex gap-4 overflow-x-auto pb-2"
          style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}>
          
          {interests.map((item) =>
          <Link
            key={item.id}
            href={item.href}
            className="flex-shrink-0 relative overflow-hidden group cursor-pointer"
            style={{ width: '220px', height: '280px', borderRadius: '16px', border: '1px solid rgba(201,168,76,0.2)' }}>
            
              <img
              src={item.image}
              alt={item.alt}
              className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110" />
            
              <div
              className="absolute inset-0 transition-opacity duration-300"
              style={{
                background:
                'linear-gradient(to top, rgba(26,10,0,0.82) 40%, rgba(26,10,0,0.1) 100%)'
              }} />
            
              <div className="absolute bottom-0 left-0 right-0 p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p
                    className="font-bold text-base"
                    style={{ fontFamily: '"Playfair Display", Georgia, serif', color: '#E8C97A' }}>
                    
                      {item.label}
                    </p>
                    <p className="text-xs mt-0.5" style={{ color: 'rgba(250,247,242,0.6)' }}>{item.count}</p>
                  </div>
                  <div
                    className="w-7 h-7 rounded-full flex items-center justify-center transition-colors"
                    style={{ background: 'rgba(201,168,76,0.25)', border: '1px solid rgba(201,168,76,0.4)' }}
                    onMouseEnter={(e) => (e.currentTarget.style.background = 'rgba(201,168,76,0.45)')}
                    onMouseLeave={(e) => (e.currentTarget.style.background = 'rgba(201,168,76,0.25)')}>
                    <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                      <path
                      d="M3 6H9M9 6L6.5 3.5M9 6L6.5 8.5"
                      stroke="#C9A84C"
                      strokeWidth="1.2"
                      strokeLinecap="round"
                      strokeLinejoin="round" />
                    
                    </svg>
                  </div>
                </div>
              </div>
            </Link>
          )}
        </div>
      </div>
    </div>);

}